import { de } from '@/components/_forms/_helpers/Components/CountrySelector/localization/de.json'
import { en } from '@/components/_forms/_helpers/Components/CountrySelector/localization/en.json'
import { es } from '@/components/_forms/_helpers/Components/CountrySelector/localization/es.json'
import { fr } from '@/components/_forms/_helpers/Components/CountrySelector/localization/fr.json'
import { it } from '@/components/_forms/_helpers/Components/CountrySelector/localization/it.json'
import { nl } from '@/components/_forms/_helpers/Components/CountrySelector/localization/nl.json'
import {
  getFlattenedCountryList,
  getFlattenedExternalCountryList,
} from '@/constants/pickerRegions/regions'
import type { Alpha2CountryCode } from '@/lib/types/alpha2CountryCode'
import type { SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'
import useGlobalStore from '@/store/useGlobalStore'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type CountrySearchResult = {
  code: SupportedAlpha2CountryCode
  name: string
  languages: readonly SupportedLanguage[]
}

type UseCountrySearchReturn = {
  searchValue: string
  searchResults: CountrySearchResult[]
  isSearching: boolean
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export const useCountrySearch = (): UseCountrySearchReturn => {
  const {
    app: { language },
  } = useGlobalStore()

  const [searchValue, setSearchValue] = useState<string>('')
  const [isSearching, setIsSearching] = useState<boolean>(false)

  useEffect(() => {
    if (searchValue.trim()) {
      setIsSearching(true)
    } else {
      setIsSearching(false)
    }
  }, [searchValue])

  const allCountries = useMemo((): CountrySearchResult[] => {
    const supportedMarkets = getFlattenedCountryList()
    const externalMarkets = getFlattenedExternalCountryList()

    const flattenedCountries = {
      ...supportedMarkets,
      ...externalMarkets,
    }

    const COUNTRY_NAMES = {
      en,
      es,
      fr,
      de,
      it,
      nl,
      // External languages that are supported, but not in shop2 at the moment
      ar: en,
      id: en,
      ja: en,
      km: en,
      ko: en,
      tr: en,
      mn: en,
      th: en,
    }[language]

    return Object.entries(flattenedCountries).map(([code, languages]) => ({
      code: code as SupportedAlpha2CountryCode,
      name: COUNTRY_NAMES[code as Alpha2CountryCode] || code,
      languages: languages as readonly SupportedLanguage[],
    }))
  }, [language])

  const searchResults = useMemo((): CountrySearchResult[] => {
    if (!searchValue.trim()) {
      return []
    }

    const searchTerm = searchValue.toLowerCase().trim()

    return allCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchTerm) ||
        country.code.toLowerCase().includes(searchTerm),
    )
  }, [searchValue, allCountries])

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setSearchValue(value)
    },
    [],
  )

  return {
    searchValue,
    searchResults,
    isSearching,
    handleSearchChange,
  }
}
